import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRightLeft } from "lucide-react";

export default function Transfers() {
  const qc = useQueryClient();
  const [toId, setToId] = useState("");
  const [invId, setInvId] = useState("");
  const [qty, setQty] = useState("1");
  const [notes, setNotes] = useState("");
  const { data: transfers = [] } = useQuery({ queryKey: ["transfers"], queryFn: async () => (await api.get("/transfers")).data });
  const { data: inventory = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const { data: org } = useQuery({ queryKey: ["org"], queryFn: async () => (await api.get("/org")).data });
  const outlets = (org?.outlets || []).filter((o) => o.id !== org?.current_restaurant_id);

  const send = async () => {
    if (!toId || !invId) return toast.error("Pick outlet and item");
    const item = inventory.find((i) => i.id === invId);
    try {
      await api.post("/transfers", {
        to_restaurant_id: toId,
        notes,
        items: [{ inventory_id: invId, name: item?.name, qty: Number(qty), unit: item?.unit }],
      });
      toast.success("Stock transferred");
      setQty("1"); setNotes("");
      qc.invalidateQueries({ queryKey: ["transfers"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Transfer failed"); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="transfers">
      <PageHeader title="Central kitchen transfers" subtitle="Move stock between outlets in your chain" />
      <div className="kul-card p-5 max-w-xl space-y-3 mb-6">
        <div>
          <Label>Destination outlet</Label>
          <select className="w-full h-10 border rounded-lg px-2 text-sm" value={toId} onChange={(e) => setToId(e.target.value)}>
            <option value="">Select outlet</option>
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <Label>Inventory item</Label>
          <select className="w-full h-10 border rounded-lg px-2 text-sm" value={invId} onChange={(e) => setInvId(e.target.value)}>
            <option value="">Select item</option>
            {inventory.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.stock} {i.unit})</option>)}
          </select>
        </div>
        <div><Label>Qty</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
        <div><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <Button onClick={send} className="bg-[var(--kul-primary)] text-white"><ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer</Button>
        {!outlets.length && <p className="text-xs text-neutral-400">Add another outlet under Chain to enable transfers.</p>}
      </div>
      <div className="space-y-2 max-w-2xl">
        {transfers.map((t) => (
          <div key={t.id} className="kul-card p-4 text-sm">
            <div className="font-medium">→ {t.to_restaurant_name || t.to_restaurant_id}</div>
            <div className="text-neutral-500">{(t.items || []).map((i) => `${i.qty} ${i.unit || ""} ${i.name}`).join(", ")}</div>
            <div className="text-xs text-neutral-400 mt-1">{t.created_by} · {t.created_at}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
